import { useState, useRef, useEffect } from 'react';
import { AppPage, AppCard } from '../../components/general';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import StatusIncidentService from '../../services/statusIncidentService';
import {
  StatusIncident,
  CreateOrUpdateStatusIncident,
  IncidentSeverity,
  AffectedComponent,
} from '../../models/statusIncident';
import { IntegrationVendor } from '../../models/enums/integrationEnums';

export const StatusIncidents = (): JSX.Element => {
  const toast = useRef<Toast>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [incidents, setIncidents] = useState<StatusIncident[]>([]);
  const [editorVisible, setEditorVisible] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<StatusIncident | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [severity, setSeverity] = useState<IncidentSeverity | null>(null);
  const [affectedComponent, setAffectedComponent] = useState<AffectedComponent | null>(null);
  const [affectedVendor, setAffectedVendor] = useState<IntegrationVendor | null>(null);

  const service = StatusIncidentService.getInstance();

  useEffect(() => {
    fetchIncidents();
  }, []);

  const fetchIncidents = async () => {
    setIsLoading(true);
    try {
      const data = await service.getAllIncidents();
      setIncidents(data);
    } catch (err: any) {
      showError(err.message || 'Failed to load incidents');
    } finally {
      setIsLoading(false);
    }
  };

  const showMessage = (severity: 'success' | 'error', summary: string, detail: string) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const showError = (message: string) => showMessage('error', 'Error', message);

  const resetForm = () => {
    setTitle('');
    setDetail('');
    setStartDate(null);
    setEndDate(null);
    setSeverity(null);
    setAffectedComponent(null);
    setAffectedVendor(null);
    setSelectedIncident(null);
  };

  const openCreate = () => {
    resetForm();
    setStartDate(new Date());
    setEditorVisible(true);
  };

  const openEdit = (incident: StatusIncident) => {
    setSelectedIncident(incident);
    setTitle(incident.title);
    setDetail(incident.detail || '');
    setStartDate(new Date(incident.startDate));
    setEndDate(incident.endDate ? new Date(incident.endDate) : null);
    setSeverity(incident.severity);
    setAffectedComponent(incident.affectedComponent);
    setAffectedVendor(incident.affectedVendor || null);
    setEditorVisible(true);
  };

  const closeIncident = async (incident: StatusIncident) => {
    try {
      await service.updateIncident(incident.statusIncidentId, {
        title: incident.title,
        detail: incident.detail,
        startDate: incident.startDate,
        endDate: new Date(),
        severity: incident.severity,
        affectedComponent: incident.affectedComponent,
        affectedVendor: incident.affectedVendor,
      });
      showMessage('success', 'Success', 'Incident closed');
      fetchIncidents();
    } catch (err: any) {
      showError(err.message || 'Failed to close incident');
    }
  };

  const handleSave = async () => {
    if (!title || !startDate || !severity || !affectedComponent) {
      showError('Please fill in all required fields');
      return;
    }
    if (affectedComponent === AffectedComponent.Vendor && !affectedVendor) {
      showError('Affected Vendor is required when component is Vendor');
      return;
    }

    setIsSubmitting(true);
    const payload: CreateOrUpdateStatusIncident = {
      title,
      detail: detail || undefined,
      startDate,
      endDate: endDate || undefined,
      severity,
      affectedComponent,
      affectedVendor: affectedComponent === AffectedComponent.Vendor ? affectedVendor || undefined : undefined,
    };

    try {
      if (selectedIncident) {
        await service.updateIncident(selectedIncident.statusIncidentId, payload);
        showMessage('success', 'Success', 'Incident updated');
      } else {
        await service.createIncident(payload);
        showMessage('success', 'Success', 'Incident created');
      }
      setEditorVisible(false);
      resetForm();
      fetchIncidents();
    } catch (err: any) {
      showError(err.message || 'Failed to save incident');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (incident: StatusIncident) => {
    confirmDialog({
      message: `Are you sure you want to delete "${incident.title}"?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await service.deleteIncident(incident.statusIncidentId);
          showMessage('success', 'Success', 'Incident deleted');
          fetchIncidents();
        } catch (err: any) {
          showError(err.message || 'Failed to delete incident');
        }
      },
    });
  };

  const getSeverityTag = (sev: IncidentSeverity) => {
    switch (sev) {
      case IncidentSeverity.Critical:
        return <Tag value="Critical" severity="danger" />;
      case IncidentSeverity.Degraded:
        return <Tag value="Degraded" severity="warning" />;
      case IncidentSeverity.Info:
        return <Tag value="Info" severity="info" />;
    }
  };

  const getStatusLabel = (incident: StatusIncident): JSX.Element => {
    if (!incident.endDate) {
      return <Tag value="Active" severity="danger" />;
    }
    return <Tag value="Resolved" severity="success" />;
  };

  const formatDateTime = (date: Date): string => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const severityOptions = [
    { label: 'Info', value: IncidentSeverity.Info },
    { label: 'Degraded', value: IncidentSeverity.Degraded },
    { label: 'Critical', value: IncidentSeverity.Critical },
  ];

  const componentOptions = [
    { label: 'Mezzo', value: AffectedComponent.Mezzo },
    { label: 'Vendor', value: AffectedComponent.Vendor },
    { label: 'Other', value: AffectedComponent.Other },
  ];

  const vendorOptions = Object.values(IntegrationVendor)
    .filter((v) => v !== IntegrationVendor.None)
    .map((v) => ({ label: v, value: v }));

  return (
    <AppPage title="Status Incidents" breadcrumbs="Super Admin - Status Incidents">
      <AppCard>
        <Toast ref={toast} />
        <ConfirmDialog />

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Manage Incidents</h2>
          <Button label="Create Incident" icon="pi pi-plus" onClick={openCreate} />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} />
          </div>
        ) : incidents.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <i className="pi pi-check-circle text-4xl text-green-500 mb-3 block" />
            <p>No incidents recorded</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {incidents.map((incident) => (
              <div
                key={incident.statusIncidentId}
                className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusLabel(incident)}
                      {getSeverityTag(incident.severity)}
                      <span className="font-medium">{incident.title}</span>
                    </div>
                    {incident.detail && (
                      <p className="text-sm text-gray-600 mb-2">{incident.detail}</p>
                    )}
                    <div className="text-xs text-gray-400 flex flex-wrap gap-3">
                      <span>Component: {incident.affectedComponent}</span>
                      {incident.affectedVendor && <span>Vendor: {incident.affectedVendor}</span>}
                      <span>Started: {formatDateTime(incident.startDate)}</span>
                      {incident.endDate && <span>Ended: {formatDateTime(incident.endDate)}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 ml-4">
                    {!incident.endDate && (
                      <Button
                        icon="pi pi-check"
                        className="p-button-rounded p-button-text p-button-success p-button-sm"
                        tooltip="Close Incident"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => closeIncident(incident)}
                      />
                    )}
                    <Button
                      icon="pi pi-pencil"
                      className="p-button-rounded p-button-text p-button-sm"
                      tooltip="Edit"
                      tooltipOptions={{ position: 'top' }}
                      onClick={() => openEdit(incident)}
                    />
                    <Button
                      icon="pi pi-trash"
                      className="p-button-rounded p-button-text p-button-danger p-button-sm"
                      tooltip="Delete"
                      tooltipOptions={{ position: 'top' }}
                      onClick={() => handleDelete(incident)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog
          header={selectedIncident ? 'Edit Incident' : 'Create Incident'}
          visible={editorVisible}
          onHide={() => {
            setEditorVisible(false);
            resetForm();
          }}
          style={{ width: '550px', maxWidth: '95vw' }}
          footer={
            <div className="flex justify-end gap-2">
              <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-button-text"
                onClick={() => {
                  setEditorVisible(false);
                  resetForm();
                }}
              />
              <Button
                label={selectedIncident ? 'Update' : 'Create'}
                icon="pi pi-check"
                onClick={handleSave}
                loading={isSubmitting}
              />
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <InputText
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full"
                placeholder="Enter incident title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Detail</label>
              <InputTextarea
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                className="w-full"
                rows={3}
                placeholder="Enter incident details"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <Calendar
                  value={startDate}
                  onChange={(e) => setStartDate(e.value as Date)}
                  showTime
                  hourFormat="12"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <Calendar
                  value={endDate}
                  onChange={(e) => setEndDate(e.value as Date)}
                  showTime
                  hourFormat="12"
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Severity <span className="text-red-500">*</span>
                </label>
                <Dropdown
                  value={severity}
                  options={severityOptions}
                  onChange={(e) => setSeverity(e.value)}
                  placeholder="Select severity"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Affected Component <span className="text-red-500">*</span>
                </label>
                <Dropdown
                  value={affectedComponent}
                  options={componentOptions}
                  onChange={(e) => {
                    setAffectedComponent(e.value);
                    if (e.value !== AffectedComponent.Vendor) {
                      setAffectedVendor(null);
                    }
                  }}
                  placeholder="Select component"
                  className="w-full"
                />
              </div>
            </div>

            {affectedComponent === AffectedComponent.Vendor && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Affected Vendor <span className="text-red-500">*</span>
                </label>
                <Dropdown
                  value={affectedVendor}
                  options={vendorOptions}
                  onChange={(e) => setAffectedVendor(e.value)}
                  placeholder="Select vendor"
                  className="w-full"
                  filter
                />
              </div>
            )}
          </div>
        </Dialog>
      </AppCard>
    </AppPage>
  );
};
